/**
 * BrandingProvider — fetches the tenant's branding once on mount and
 * applies it to the document as CSS variables + a global window object
 * that <LivingBackground /> reads from.
 *
 * Why a window-global as well as a React context?
 *   - LivingBackground is mounted at the App root and used by both
 *     authenticated and PUBLIC routes (Login, Marketing). The public
 *     routes can't read the React context without a wrapping fetch, so
 *     we mirror the branding onto `window.__VE_BRANDING__` and emit a
 *     `ve-branding-changed` event when it updates.  Components inside
 *     the authed tree can still use `useBranding()` for ergonomics.
 *
 * CSS variables set on :root:
 *   --brand-primary    (tenant primary_color, fallback #AE52EA)
 *   --brand-accent     (tenant accent_color, fallback primary)
 *   --brand-primary-soft  (rgba primary @ .15 alpha)
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const BrandingContext = createContext(null);

function hexToRgb(hex) {
  if (!hex) return [174, 82, 234];
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function applyToDocument(branding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const primary = branding?.primary_color || '#AE52EA';
  const accent = branding?.accent_color || primary;
  const [r, g, b] = hexToRgb(primary);
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-accent', accent);
  root.style.setProperty('--brand-primary-soft', `rgba(${r}, ${g}, ${b}, 0.15)`);
  root.style.setProperty('--brand-primary-strong', `rgba(${r}, ${g}, ${b}, 0.45)`);
  if (typeof window !== 'undefined') {
    window.__VE_BRANDING__ = branding;
    window.dispatchEvent(new CustomEvent('ve-branding-changed'));
  }
  // Update the PWA manifest link so when the user taps "Install as app",
  // the home-screen icon + name reflect THEIR tenant's brand, not the
  // platform default. The backend endpoint reads ?tenant=<id> and emits
  // a white-label manifest. Adding a cache-buster (logo_hash or now) so
  // Chrome re-fetches the manifest after a rebrand.
  try {
    const link = document.getElementById('ve-manifest');
    if (link) {
      const tid = branding?.tenant_id;
      const bust = branding?.logo_hash || branding?.primary_color || 'v1';
      const qs = tid && tid !== 'primary'
        ? `?tenant=${encodeURIComponent(tid)}&v=${encodeURIComponent(bust)}`
        : `?v=${encodeURIComponent(bust)}`;
      const next = `/api/tenants/branding/manifest.json${qs}`;
      if (link.getAttribute('href') !== next) link.setAttribute('href', next);
    }
  } catch { /* manifest swap is decorative — never fail boot */ }
}

const DEFAULT_BRANDING = {
  primary_color: '#AE52EA',
  accent_color: null,
  logo_url: null,
  brand_name: 'Vigilant Entities',
  logo_auto_transparent: true,
  live_bg_preset: 'particles',
  live_bg_video_url: null,
  live_bg_opacity: 0.6,
};

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  const refresh = useCallback(async () => {
    try {
      // If the user is logged in, fetch THEIR tenant's branding.
      // Otherwise hit the public endpoint so anonymous visitors on the
      // marketing pages still see the platform's uploaded logo, not
      // the hard-coded SVG fallback. Post-cookie-migration we can't
      // read the HttpOnly session_token from JS, so we just try the
      // authenticated endpoint first and fall back to public on 401.
      let data;
      try {
        ({ data } = await api.get('/tenants/me/branding'));
      } catch (e) {
        if (e?.response?.status === 401) {
          ({ data } = await api.get('/tenants/branding/public'));
        } else {
          throw e;
        }
      }
      const merged = { ...DEFAULT_BRANDING, ...(data || {}) };
      applyToDocument(merged);
      queueMicrotask(() => setBranding(merged));
    } catch {
      // Last-resort fallback when both endpoints fail.
      applyToDocument(DEFAULT_BRANDING);
      queueMicrotask(() => setBranding(DEFAULT_BRANDING));
    }
  }, []);

  useEffect(() => {
    applyToDocument(DEFAULT_BRANDING);
    refresh();
    // Re-fetch tenant branding (which drives the PWA manifest swap)
    // whenever the auth identity changes — login, logout, signup,
    // Google session exchange.
    const onAuth = () => refresh();
    if (typeof window !== 'undefined') {
      window.addEventListener('ve-auth-changed', onAuth);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('ve-auth-changed', onAuth);
      }
    };
  }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, refresh, setLocal: (b) => { setBranding(b); applyToDocument(b); } }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext) || { branding: DEFAULT_BRANDING, refresh: () => {}, setLocal: () => {} };
}
